import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  GraduationCap, 
  TrendingUp, 
  ArrowRight, 
  Play,
  Star,
  BookOpen,
  Award,
  Heart,
  Target
} from 'lucide-react';
import { Button } from '../../components/ui/button.jsx';
import { Card, CardContent } from '../../components/ui/card.jsx';
import { Badge } from '../../components/ui/badge.jsx';
import { HeroGeometric } from '../../components/ui/shape-landing-hero.jsx';

const Home = () => {
  const navigate = useNavigate();


  const stats = [
    { 
      number: '500+', 
      label: 'Students', 
      icon: Users, 
      description: 'Active learners'
    },
    { 
      number: '50+', 
      label: 'Teachers', 
      icon: GraduationCap, 
      description: 'Expert educators'
    },
    { 
      number: '15+', 
      label: 'Classes', 
      icon: BookOpen, 
      description: 'Grade levels'
    },
    { 
      number: '95%', 
      label: 'Success Rate', 
      icon: TrendingUp, 
      description: 'Academic excellence'
    },
  ];

  const features = [
    {
      title: 'Quality Education',
      description: 'Comprehensive curriculum designed to nurture academic excellence and critical thinking skills.',
      icon: BookOpen,
      color: 'text-blue-600'
    },
    {
      title: 'Experienced Faculty',
      description: 'Dedicated teachers with years of experience committed to student success.',
      icon: Award,
      color: 'text-emerald-600'
    },
    {
      title: 'Holistic Development',
      description: 'Focus on character building, moral values, and overall personality development.',
      icon: Heart,
      color: 'text-purple-600'
    },
    {
      title: 'Modern Facilities',
      description: 'State-of-the-art infrastructure and learning resources for optimal education.',
      icon: Target,
      color: 'text-orange-600'
    },
  ];

  const testimonials = [
    {
      name: 'Mrs. Adebayo',
      role: 'Parent',
      content: 'Bridge of Life Academy has transformed my child\'s learning experience. The teachers are exceptional.',
      rating: 5
    },
    {
      name: 'Mr. Johnson',
      role: 'Parent',
      content: 'The holistic approach to education here is remarkable. My daughter loves going to school every day.',
      rating: 5
    },
    {
      name: 'Mrs. Okafor',
      role: 'Parent',
      content: 'Excellent academic standards and wonderful character development programs.',
      rating: 5
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroGeometric 
        badge="Excellence in Education Since 2010"
        title1="Bridge of Life"
        title2="Academy"
        description="Empowering young minds with quality education, moral values, and the skills needed to excel in an ever-changing world."
      />

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <Card className="text-center hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-6">
                      <div className="flex justify-center mb-4">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                          <Icon className="w-8 h-8 text-blue-600" />
                        </div>
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</h3>
                      <p className="text-lg font-semibold text-gray-700 mb-1">{stat.label}</p>
                      <p className="text-sm text-gray-500">{stat.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge variant="outline" className="mb-4">Why Choose Us</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Excellence in Every Aspect
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide a comprehensive educational experience that goes beyond academics 
              to develop well-rounded individuals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="p-6">
                      <div className="mb-4">
                        <Icon className={`w-12 h-12 ${feature.color}`} />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge variant="outline" className="mb-4">Testimonials</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Parents Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from the parents who trust us with their children's education.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card className="h-full">
                  <CardContent className="p-6">
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <p className="text-gray-600 mb-4 italic">"{testimonial.content}"</p>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.role}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Join Our School Family?
            </h2>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Take the first step towards your child's bright future. Contact us today 
              to learn more about our admission process.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-blue-900 hover:bg-gray-100"
                onClick={() => navigate('/contact')}
              >
                Apply Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-slate-900"
                onClick={() => navigate('/contact')}
              >
                Schedule Visit
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;